import { sharedHydrated } from './shared-actions'
import { createEnvelope, readEnvelope, STORAGE_KEY, writeEnvelope } from './shared-state'
import { selectSharedData } from './selectors'
import type { AppStore } from './create-store'
import { revisionCommitted } from './root-reducer'
import { type SyncContext, withWriteLock } from './sync-context'

interface SyncMessage {
  type: 'shared-updated'
  revision: number
  origin: string
}

/** Tells the other tabs a new revision is in storage. */
export function broadcastRevision(ctx: SyncContext, revision: number) {
  const message: SyncMessage = { type: 'shared-updated', revision, origin: ctx.tabId }
  try {
    ctx.channel?.postMessage(message)
  } catch {
    // The `storage` event fallback still reaches other tabs.
  }
}

/**
 * Brings Redux up to the latest stored revision. Returns whether anything was applied.
 * Only reads storage and updates Redux; it never writes, so a sync can't cause another sync.
 * Comparing revisions makes duplicate events (BroadcastChannel + `storage` both fire) harmless.
 */
export function applyStoredState(store: AppStore, ctx: SyncContext): boolean {
  const stored = readEnvelope(ctx.local)
  if (!stored || stored.revision <= store.getState().sync.revision) return false
  store.dispatch(sharedHydrated({ data: stored.data, revision: stored.revision }))
  return true
}

/**
 * Starts cross-tab synchronization for a store: seeds storage if it is empty, then listens for other tabs'
 * changes via BroadcastChannel (immediate) and the window `storage` event (fallback). Returns a stop function.
 */
export function startSync(store: AppStore, ctx: SyncContext): () => void {
  let stopped = false

  // Seed once. Re-check inside the lock: another tab may have seeded or already changed data meanwhile.
  void withWriteLock(ctx, () => {
    if (stopped) return
    if (readEnvelope(ctx.local)) {
      applyStoredState(store, ctx)
      return
    }
    const revision = Math.max(store.getState().sync.revision, 1)
    try {
      writeEnvelope(ctx.local, createEnvelope(selectSharedData(store.getState()), revision, ctx.tabId))
      store.dispatch(revisionCommitted(revision))
    } catch {
      // Storage unavailable: the app still works from the in-memory seed for this tab.
    }
  })

  const onMessage = (event: MessageEvent<SyncMessage>) => {
    const message = event.data
    if (message?.type !== 'shared-updated' || message.origin === ctx.tabId) return
    applyStoredState(store, ctx)
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) applyStoredState(store, ctx)
  }
  // Catch up if a tab was in the background and missed events.
  const onFocus = () => applyStoredState(store, ctx)

  ctx.channel?.addEventListener('message', onMessage)
  window.addEventListener('storage', onStorage)
  window.addEventListener('focus', onFocus)

  return () => {
    stopped = true
    ctx.channel?.removeEventListener('message', onMessage)
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('focus', onFocus)
  }
}
